import { assign, createMachine, raise, sendTo, spawn } from 'xstate';
import { accountActivityCacheKey, queryFnFactory } from '@syf-api-library/account-activity';
import makeAPaymentStore from '../store';
import { conversionCountAtom } from '../store/conversionCountAtom';
import { accountActivityDataAtom, apiConfigAtom } from '../api/accountActivity';
import { PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../const/analytics';
import { paymentTypes } from '../const/paymentOptions';
import { getMaximumPaymentDays } from '../helpers/getEndDate';
import isDCHSettlementEnrolled from '../helpers/isDCHSettlementEnrolled';
import { getOneTimePaymentEligibilityDetail } from '../helpers/parseEligibility';
import { isSecuredInstallmentLoanProduct } from '../helpers/productTypeHelpers';
import shouldEnableManagePaymentMethods from '../helpers/shouldEnableManagePaymentMethods';
import { MAPFlowTypes } from '../typings/deepLink';
import apiMachine from './apiMachine/apiMachine';
import autopayMachine from './autopay/autopayMachine';
import oneTimePaymentMachine from './oneTimePayment/oneTimePaymentMachine';
import addPaymentMethodMachine from './paymentMethod/addPaymentMethodMachine';
import paymentMethodMachine from './paymentMethod/paymentMethodMachine';
import payoffPaymentMachine from './payoffPayment/payoffPaymentMachine';
import pendingPaymentMachine from './pendingPayment/pendingPaymentMachine';
import autopayBannerMachine from './autopayBannerMachine';
const PAYMENT_FLOW_ID = 'paymentFlow';
const mainMachine = createMachine({
    predictableActionArguments: true,
    preserveActionOrder: true,
    id: 'mainMachine',
    context: {
        apiConfig: undefined,
        apiMachineRef: undefined,
        currentPaymentFlow: [null, null],
        featureFlags: undefined,
        helpNumber: undefined,
        triggerAnalyticsEvent: undefined,
        additionalProps: undefined
    },
    schema: {
        events: {}
    },
    initial: 'open',
    states: {
        closed: {
            id: 'closed',
            type: 'final',
            entry: ({ conversionCount, handleClose, handleCloseOnConversion }) => {
                if (handleCloseOnConversion && conversionCount > 0) {
                    handleCloseOnConversion();
                }
                if (handleClose) {
                    handleClose();
                }
            }
        },
        open: {
            id: 'open',
            initial: 'initializing',
            states: {
                initializing: {
                    id: 'initializing',
                    initial: 'fetchApiData',
                    states: {
                        fetchApiData: {
                            id: 'fetchApiData',
                            initial: 'fetchAccountSummary',
                            states: {
                                fetchAccountSummary: {
                                    entry: [
                                        'spawnApiMachine',
                                        sendTo('apiMachine', { type: 'FETCH_ACCOUNT_SUMMARY' })
                                    ],
                                    on: {
                                        AFTER_SUMMARY_FETCH: [
                                            {
                                                cond: 'isDelinquentAccount',
                                                target: '#open.paymentNotAllowed.delinquentAccount'
                                            },
                                            { target: 'fetchPaymentEligibility' }
                                        ]
                                    }
                                },
                                fetchPaymentEligibility: {
                                    entry: [
                                        sendTo('apiMachine', {
                                            type: 'FETCH_PAYMENT_ELIGIBILITY'
                                        })
                                    ],
                                    on: {
                                        AFTER_ELIGIBILITY_FETCH: [
                                            { target: 'fetchAccountActivity' }
                                        ]
                                    }
                                },
                                fetchAccountActivity: {
                                    invoke: {
                                        src: 'shouldInvokeAccountActivity',
                                        onDone: {
                                            target: 'fetchRemainingApis',
                                            actions: [
                                                (_, event) => {
                                                    makeAPaymentStore.set(accountActivityDataAtom, event.data);
                                                }
                                            ]
                                        },
                                        onError: {
                                            actions: raise('API_ERROR')
                                        }
                                    }
                                },
                                fetchRemainingApis: {
                                    entry: [
                                        sendTo('apiMachine', { type: 'FETCH_AUTOPAY_STATUS' }),
                                        sendTo('apiMachine', {
                                            type: 'FETCH_ALTERNATE_PAYMENT_METHODS'
                                        }),
                                        sendTo('apiMachine', { type: 'FETCH_PAYMENT_METHODS' })
                                    ],
                                    on: {
                                        AFTER_AUTOPAY_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        },
                                        AFTER_PAYMENT_METHODS_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        },
                                        AFTER_ALTERNATE_PAYMENT_METHODS_FETCH: {
                                            cond: 'hasAllDataFetchedSuccessfully',
                                            actions: raise('START_PAYMENT_FLOW')
                                        }
                                    }
                                }
                            }
                        }
                    },
                    on: {
                        START_PAYMENT_FLOW: [
                            {
                                target: 'paymentNotAllowed.oneTimePaymentNotEligible',
                                cond: 'isOneTimePaymentNotEligible',
                                actions: [
                                    'spawnPendingPaymentMachine',
                                    'spawnAutopayBannerMachine',
                                    'sendSIOneTimePaymentPendingAnalyticsEvent'
                                ]
                            },
                            {
                                target: 'paymentFlow',
                                cond: 'shouldOpenAutopayFlow',
                                actions: ['spawnAutopayMachine', 'spawnAutopayBannerMachine']
                            },
                            {
                                target: 'paymentFlow',
                                actions: [
                                    'spawnOneTimePaymentMachine',
                                    'spawnAutopayBannerMachine'
                                ]
                            }
                        ],
                        API_ERROR: {
                            target: 'error',
                            // eslint-disable-next-line no-console
                            actions: (context, event) => console.error('API_ERROR', event)
                        }
                    }
                },
                managePaymentMethod: {
                    id: 'managePaymentMethod',
                    entry: assign({
                        paymentMethodActorRef: ({ apiMachineRef, featureFlags }) => {
                            const { accountSummaryQueryResult } = apiMachineRef.getSnapshot().context;
                            const { product } = accountSummaryQueryResult.data;
                            if (shouldEnableManagePaymentMethods(product.type, featureFlags.isManagePaymentMethodsEnabled)) {
                                return spawn(paymentMethodMachine.withContext({
                                    apiMachineRef
                                }), { name: 'managePaymentMethod' });
                            }
                            return spawn(addPaymentMethodMachine.withContext({
                                apiMachineRef
                            }), { name: 'addPaymentMethod' });
                        }
                    }),
                    exit: assign({
                        paymentMethodActorRef: ({ paymentMethodActorRef }) => {
                            paymentMethodActorRef.stop();
                            return undefined;
                        }
                    })
                },
                paymentFlow: {
                    id: PAYMENT_FLOW_ID,
                    on: {
                        INCREMENT_CONVERSION: {
                            actions: [
                                assign({
                                    conversionCount: ({ conversionCount }) => conversionCount + 1
                                }),
                                ({ conversionCount }) => {
                                    makeAPaymentStore.set(conversionCountAtom, conversionCount + 1);
                                },
                                /*
                                 * autopay banner needs to know when an autopay enrollment/unenrollment
                                 * may have happened and refresh the state accordingly
                                 */
                                'sendToAutopayBanner'
                            ]
                        }
                    }
                },
                paymentNotAllowed: {
                    id: 'paymentNotAllowed',
                    states: {
                        delinquentAccount: {
                            // This state doesn't have anything to make a further transition. So, it will not have any event.
                            id: 'delinquentAccount'
                        },
                        oneTimePaymentNotEligible: {
                            id: 'oneTimePaymentNotEligible'
                        }
                    }
                },
                error: {}
            },
            on: {
                HOE_UPDATE_BANNER_VISIBILITY: {
                    actions: 'evaluateBannerVisibility'
                },
                CANCEL: '#mainMachine.closed',
                SET_ONE_TIME_PAYMENT_FLOW: {
                    actions: ['spawnOneTimePaymentMachine', 'sendToAutopayBanner'],
                    target: `open.${PAYMENT_FLOW_ID}`
                },
                SET_AUTOPAY_FLOW: {
                    actions: ['spawnAutopayMachine', 'sendToAutopayBanner'],
                    target: `open.${PAYMENT_FLOW_ID}`
                },
                SET_PAYOFF_PAYMENT_FLOW: {
                    actions: ['spawnPayoffPaymentMachine', 'sendToAutopayBanner'],
                    target: `open.${PAYMENT_FLOW_ID}`
                },
                GO_TO_PAYMENT_METHOD_FLOW: {
                    target: 'open.managePaymentMethod'
                },
                GO_TO_PAYMENT_FLOW: {
                    target: `open.${PAYMENT_FLOW_ID}`
                }
            }
        }
    }
}, {
    actions: {
        evaluateBannerVisibility: sendTo('autopayBanner', {
            type: 'UPDATE_BANNER_VISIBILITY'
        }),
        sendToAutopayBanner: sendTo('autopayBanner', (context, event) => {
            return Object.assign(Object.assign({}, event), { mainMachineContext: context });
        }),
        spawnApiMachine: assign({
            apiMachineRef: ({ apiConfig, apiMachineRef, featureFlags, helpNumber, queryClient }) => {
                if (apiMachineRef) {
                    return apiMachineRef;
                }
                const queryResult = {
                    data: undefined,
                    error: undefined,
                    isError: false
                };
                makeAPaymentStore.set(apiConfigAtom, apiConfig);
                return spawn(apiMachine.withContext({
                    accountSummaryQueryResult: queryResult,
                    alternatePaymentMethodsQueryResult: queryResult,
                    apiConfig,
                    autopayStatusQueryResult: queryResult,
                    bankNameQueryResult: queryResult,
                    featureFlags,
                    helpNumber,
                    paymentEnterpriseEligibilityQueryResult: queryResult,
                    paymentMethodsQueryResult: queryResult,
                    queryClient
                }), { name: 'apiMachine' });
            }
        }),
        spawnAutopayBannerMachine: assign({
            autopayBannerRef: (context) => {
                const { autopayBannerRef, currentPaymentFlow, apiMachineRef, featureFlags, additionalProps } = context;
                if (autopayBannerRef) {
                    autopayBannerRef.stop();
                }
                return spawn(autopayBannerMachine.withContext({
                    apiMachineRef,
                    currentPaymentFlow,
                    featureFlags,
                    additionalProps
                }), {
                    name: 'autopayBanner'
                });
            }
        }),
        spawnOneTimePaymentMachine: assign({
            currentPaymentFlow: (context) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const { apiMachineRef, featureFlags, triggerAnalyticsEvent } = context;
                const oneTimeRef = spawn(oneTimePaymentMachine.withContext({
                    apiMachineRef,
                    featureFlags,
                    shouldEnrollInOneClickAutopay: false,
                    shouldShowOneClickAutopay: false,
                    triggerAnalyticsEvent
                }));
                return [paymentTypes.ONCE, oneTimeRef];
            }
        }),
        spawnPendingPaymentMachine: assign({
            currentPaymentFlow: (context) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const pendingRef = spawn(pendingPaymentMachine);
                return [paymentTypes.PENDING, pendingRef];
            }
        }),
        spawnAutopayMachine: assign({
            currentPaymentFlow: (context, event) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const { apiMachineRef, triggerAnalyticsEvent } = context;
                const autopayActorRef = spawn(autopayMachine.withContext({
                    apiMachineRef,
                    isPaymentTypeVisible: event.isPaymentTypeVisible,
                    triggerAnalyticsEvent
                }));
                return [paymentTypes.AUTOPAY, autopayActorRef];
            }
        }),
        spawnPayoffPaymentMachine: assign({
            currentPaymentFlow: (context) => {
                const [, actorRef] = context.currentPaymentFlow;
                if (actorRef) {
                    actorRef.stop();
                }
                const { apiMachineRef, featureFlags, triggerAnalyticsEvent } = context;
                const payoffRef = spawn(payoffPaymentMachine.withContext({
                    apiMachineRef,
                    featureFlags,
                    triggerAnalyticsEvent
                }));
                return [paymentTypes.PAYOFF, payoffRef];
            }
        }),
        sendSIOneTimePaymentPendingAnalyticsEvent: (context) => {
            const { product } = context.apiMachineRef.getSnapshot().context.accountSummaryQueryResult
                .data;
            if (isSecuredInstallmentLoanProduct(product.type)) {
                const eventOptions = {
                    pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                    pageSubFunction: SUB_FUNCTION.PAYMENT_PENDING.SECURED_INSTALLMENT,
                    pageName: PAGE_NAME.PAYMENT_PENDING.ONE_TIME
                };
                context.triggerAnalyticsEvent(eventOptions);
            }
        }
    },
    services: {
        shouldInvokeAccountActivity: async ({ apiConfig, apiMachineRef, queryClient }) => {
            const { accountSummaryQueryResult } = apiMachineRef.getSnapshot().context;
            const productType = accountSummaryQueryResult.data.product.type;
            if (isSecuredInstallmentLoanProduct(productType)) {
                return undefined;
            }
            const accountActivityQuery = queryFnFactory(apiConfig.config, apiConfig.keyProps);
            return queryClient.fetchQuery({
                queryKey: accountActivityCacheKey(apiConfig.keyProps),
                queryFn: accountActivityQuery
            });
        }
    },
    guards: {
        hasAllDataFetchedSuccessfully: ({ apiMachineRef }) => {
            const accountActivityQueryResult = makeAPaymentStore.get(accountActivityDataAtom);
            const { accountSummaryQueryResult, autopayStatusQueryResult, paymentEnterpriseEligibilityQueryResult, paymentMethodsQueryResult } = apiMachineRef.getSnapshot().context;
            const productType = accountSummaryQueryResult.data.product.type;
            const allDataFetchedSuccessfully = (isSecuredInstallmentLoanProduct(productType) ||
                accountActivityQueryResult.data) &&
                accountSummaryQueryResult.data &&
                autopayStatusQueryResult.data &&
                paymentEnterpriseEligibilityQueryResult.data &&
                paymentMethodsQueryResult.data;
            return Boolean(allDataFetchedSuccessfully);
        },
        isDelinquentAccount: ({ apiMachineRef }) => {
            const { accountSummaryQueryResult } = apiMachineRef.getSnapshot().context;
            const { billing, product } = accountSummaryQueryResult.data || {};
            const { daysDelinquent } = billing;
            return getMaximumPaymentDays(daysDelinquent, product) === 0;
        },
        isOneTimePaymentNotEligible: ({ apiMachineRef, featureFlags }) => {
            if (!featureFlags.isSecuredInstallmentEnabled) {
                return false;
            }
            const { data: paymentEnterpriseEligibility } = apiMachineRef.getSnapshot().context
                .paymentEnterpriseEligibilityQueryResult;
            const oneTimePaymentEligibilityDetail = getOneTimePaymentEligibilityDetail(paymentEnterpriseEligibility, 'MAKE_A_PAYMENT');
            return (oneTimePaymentEligibilityDetail === null || oneTimePaymentEligibilityDetail === void 0 ? void 0 : oneTimePaymentEligibilityDetail.eligible) === false;
        },
        shouldOpenAutopayFlow: ({ additionalProps, apiMachineRef, featureFlags }) => {
            const { billing, product } = apiMachineRef.getSnapshot().context.accountSummaryQueryResult.data;
            const isAutopayFlow = additionalProps.flow.type === MAPFlowTypes.AUTOPAY_FLOW;
            if (isAutopayFlow &&
                isDCHSettlementEnrolled(billing, product.type, featureFlags)) {
                // eslint-disable-next-line no-console
                console.error('Product is not allowed to go under autopay flow');
                return false;
            }
            return isAutopayFlow;
        }
    }
});
export default mainMachine;
